import React, { useState } from 'react';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import max from 'lodash/max';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import BarGraph from 'components/Charts/BarGraph';
import EvolutionIcon from 'components/EvolutionIcon';
import ExportButton from 'components/Export';
import Pills from 'components/Pills';
import Select from 'components/Select';
import ChartInputsBloc from 'modules/Distribution/components/ChartInputsBloc';

const OnlineSalesBarChartContainer = styled.div`
  position: relative;

  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .row > *:not(:last-child) {
    margin-right: 15px;
  }

  .pillsContainer {
    text-transform: initial !important;
  }

  .rhcTooltipContainer {
    min-width: 350px;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    font-size: 12px;
    width: 100%;
    margin-bottom: 10px;

    > p {
      color: #abb5be;
      font-family: ${(props) => props.theme.fonts.sfcompact_text};
      font-weight: 600;
    }
  }

  .rhcTooltipValues {
    margin-left: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 75%;

    p {
      margin-left: 5px;
    }
  }

  .rhcTooltipValues > * {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 33%;
    text-align: center;
  }

  .rhcTooltipValues > div:not(:last-child) {
    margin-right: 15px;
  }

  .rhcTooltip {
    padding: 12px;
    min-width: 250px;
  }

  .rhcDate {
    color: #abb5be;
    text-transform: uppercase;
  }

  .rhcLabel {
    text-transform: uppercase;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }

  .selectSetsContainer {
    width: 80%;
    margin: 2% auto;
  }

  .setListContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: ${(props) => props.theme.fonts.sfcompact};
    margin-top: 5px;

    > div {
      margin: 0 !important;
      height: 30px !important;
      white-space: nowrap !important;
      width: initial !important;
    }

    .tag {
      width: initial !important;
    }

    > div:not(:last-child) {
      margin-right: 10px !important;
    }
  }

  @media (max-width: 1600px) {
    > div:first-child {
      flex-direction: column !important;
      align-items: flex-start;
    }

    .exportButton {
      position: absolute;
      top: -5px;
      right: 0;
    }

    .cardHeaderRight {
      justify-content: flex-start;
      align-items: center;
    }

    .row > *:not(:last-child) {
      margin-top: 2px;
    }
  }
`;

const getVintageOptions = (data, multipleRivals) => {
  const uniqueVintageValues = [...new Set(data.map((item) => item.vintage))];
  const sortedVintageValues = uniqueVintageValues.sort().reverse();
  const options = sortedVintageValues.map((item) => ({
    value: item,
    label: item
  }));

  if (!multipleRivals) {
    options.unshift({ label: I18n.t('placeholderAll'), value: null });
  }

  return options;
};

const averageData = (data) => {
  const averagedData = {};

  data.forEach((item) => {
    // Init average data
    if (!averagedData[item.vintage]) {
      averagedData[item.vintage] = {
        ...item,
        avg_price: item.avg_price,
        sumFacing: item.facing
      };
      return;
    }

    // Update average price
    ['avg_price', 'avg_local_price'].forEach((key) => {
      const beforePriceSum =
        averagedData[item.vintage][key] * averagedData[item.vintage].sumFacing;
      const toAddPriceSum = item[key] * item.facing;
      averagedData[item.vintage][key] =
        (beforePriceSum + toAddPriceSum) /
        (averagedData[item.vintage].sumFacing + item.facing);
    });

    // Update sum facing
    averagedData[item.vintage].sumFacing += item.facing;

    // Update min values
    if (item[`min_price`] < averagedData[item.vintage]['min_price']) {
      averagedData[item.vintage]['min_price'] = item['min_price'];
      averagedData[item.vintage]['min_store'] = item['min_store'];
    }
    if (
      item['min_local_price'] < averagedData[item.vintage]['min_local_price']
    ) {
      averagedData[item.vintage]['min_local_price'] = item['min_local_price'];
    }

    // Update max values
    if (item[`max_price`] > averagedData[item.vintage]['max_price']) {
      averagedData[item.vintage]['max_price'] = item['max_price'];
      averagedData[item.vintage]['max_store'] = item['max_store'];
    }
    if (
      item['max_local_price'] > averagedData[item.vintage]['max_local_price']
    ) {
      averagedData[item.vintage]['max_local_price'] = item['max_local_price'];
    }
  });

  return Object.values(averagedData);
};

const filterData = (
  data,
  multipleRivals,
  vintage,
  vintageEnd,
  vintageStart
) => {
  let filteredData = [...data];
  if (multipleRivals) {
    filteredData =
      vintage && vintage.value !== undefined
        ? filteredData.filter((item) => item.vintage === vintage.value)
        : filteredData;
  } else {
    filteredData = filteredData.filter(
      (item) =>
        (!vintageEnd ||
          !vintageEnd.value ||
          item.vintage <= vintageEnd.value) &&
        (!vintageStart ||
          !vintageStart.value ||
          item.vintage >= vintageStart.value)
    );
  }
  return filteredData;
};

const getAverage = (data = []) => {
  if (!data || data.length === 0) {
    return 0;
  }

  const facingData = data.filter((item) => item.facing);
  const valueData = data.filter(
    (item) => item.facing && item.value && item.value.value
  );

  const sumFacing = facingData.reduce((acc, item) => acc + item.facing, 0);
  const sumValueFacing = valueData.reduce(
    (acc, item) => acc + item.value.value * item.facing,
    0
  );

  return sumValueFacing / sumFacing;
};

const getMaxValue = (data) => {
  const maxPrices = data
    .filter((item) => item.maxPrice && item.maxPrice.value)
    .map((item) => item.maxPrice.value);
  return maxPrices.length > 0 ? Math.max(...maxPrices) : 0;
};

const getMinValue = (data) => {
  const minPrices = data
    .filter((item) => item.minPrice && item.minPrice.value)
    .map((item) => item.minPrice.value);
  return minPrices.length > 0 ? Math.min(...minPrices) : 0;
};

const getLabels = (data) => {
  if (!data || data.length === 0) {
    return [];
  }
  return data.map((set) => (set && set.length > 0 ? set[0].wine_name : ''));
};

const getRenderTooltip = (showLocalCurrency) => {
  const renderTooltip = (overData) => (
    <div className="rhcTooltip">
      <div className="rhcTooltipContainer">
        <div className="rhcTooltipValues">
          <p className="rhcLabel">{I18n.t('lowPrice')}</p>
          <p className="rhcLabel">{I18n.t('averagePrice')}</p>
          <p className="rhcLabel">{I18n.t('highPrice')}</p>
        </div>
      </div>
      {overData
        .filter((set) => set && set.value)
        .map((set, index) => (
          <React.Fragment key={[set.name, set.wine_name, index].join('-')}>
            <div className="rhcTooltipContainer">
              <p>{`${set.wine_name} ${set.name}`}</p>
              <div className="rhcTooltipValues">
                {Object.keys(set).map(
                  (key) =>
                    set[key] &&
                    set[key].value && (
                      <div
                        key={[set.name, set.wine_name, index, key].join('-')}
                      >
                        <EvolutionIcon icon={set[key].icon} />
                        <p>
                          {set[key].value.toLocaleString('fr-FR', {
                            style: 'currency',
                            currency: showLocalCurrency
                              ? get(set, 'local_currency', 'EUR')
                              : get(set, 'displayed_currency', 'EUR'),
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                          })}
                        </p>
                      </div>
                    )
                )}
              </div>
            </div>
            {index === 0 && (
              <div className="rhcTooltipContainer">
                <div className="rhcTooltipValues">
                  <p className="">{set.min_store}</p>
                  <p className="" />
                  <p className="">{set.max_store}</p>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
    </div>
  );
  return renderTooltip;
};

const SelectRivals = ({
  multipleRivals,
  rivalSelection,
  rivalList,
  handleRivalSelection
}) => (
  <div className="selectSetsContainer ignored-export">
    <Select
      placeholder={
        multipleRivals
          ? I18n.t('placeholderRivals')
          : I18n.t('placeholderRival')
      }
      isMulti={multipleRivals}
      value={rivalSelection}
      isClearable
      dropdownProps={{
        styles: `margin-left:20px;margin-right:20px;`
      }}
      onChange={(value) => handleRivalSelection(value)}
      options={rivalList.map((item) => ({ value: item, label: item }))}
    />
  </div>
);

const getDataMaxDomain = (data, showLocalCurrency) => {
  const dataMax = max(
    flatten(
      [...data].map((set) =>
        set.map((item) =>
          showLocalCurrency && item.avg_local_price
            ? item.avg_local_price
            : item.avg_price
        )
      )
    )
  );
  if (dataMax) {
    const dataMaxDomain = Math.round((dataMax + dataMax / 5) / 100) * 100;
    if (dataMaxDomain <= dataMax) {
      return dataMaxDomain + 50;
    }
    return dataMaxDomain;
  }
  return 0;
};

const formatData = ({
  data,
  dataClient,
  allMarket,
  showLocalCurrency,
  multipleRivals,
  rivalSelection,
  vintage,
  vintageEnd,
  vintageStart,
  activePill
}) => {
  let formattedData = [
    allMarket ? averageData([...dataClient]) : [...dataClient]
  ];

  if (multipleRivals && rivalSelection.length > 0) {
    const rivals = rivalSelection.map((item) => item.label);
    rivals.forEach((rival) => {
      const rivalData = allMarket
        ? averageData(data.filter((item) => item.wine_name === rival))
        : data.filter((item) => item.wine_name === rival);
      formattedData.push(rivalData);
    });
  } else if (!multipleRivals && rivalSelection) {
    const rivalData = allMarket
      ? averageData(
          data.filter((item) => item.wine_name === rivalSelection.value)
        )
      : data.filter((item) => item.wine_name === rivalSelection.value);
    formattedData.push(rivalData);
  }

  formattedData = [...formattedData].map((set) => {
    set = filterData(set, multipleRivals, vintage, vintageEnd, vintageStart);
    return set
      .map((item) =>
        activePill === 'avg_price'
          ? {
              ...item,
              name: item.vintage,
              minPrice: {
                icon: 'down',
                value:
                  showLocalCurrency && item.min_local_price
                    ? item.min_local_price
                    : item.min_price
              },
              value: {
                icon: 'neutral',
                value:
                  showLocalCurrency && item.avg_local_price
                    ? item.avg_local_price
                    : item.avg_price
              },
              maxPrice: {
                icon: 'up',
                value:
                  showLocalCurrency && item.max_local_price
                    ? item.max_local_price
                    : item.max_price
              }
            }
          : {
              ...item,
              name: item.vintage,
              value: {
                icon: 'down',
                value:
                  showLocalCurrency && item.min_local_price
                    ? item.min_local_price
                    : item.min_price
              },
              avgPrice: {
                icon: 'neutral',
                value:
                  showLocalCurrency && item.avg_local_price
                    ? item.avg_local_price
                    : item.avg_price
              },
              maxPrice: {
                icon: 'up',
                value:
                  showLocalCurrency && item.max_local_price
                    ? item.max_local_price
                    : item.max_price
              }
            }
      )
      .sort(function (a, b) {
        return parseInt(a.vintage, 10) - parseInt(b.vintage, 10);
      });
  });

  let setNames = [];
  let wines = [];
  formattedData.forEach((set) => {
    if (set && set.length > 0) {
      set.sort(function (a, b) {
        return Number(a.name) - Number(b.name);
      });
      setNames.push(...set.map((item) => item.name));
      wines.push(get(set, '[0].wine_name'));
    }
  });

  setNames = [...new Set(setNames)].sort();
  formattedData.forEach((set, setIndex) => {
    setNames.forEach((name, index) => {
      if (!set[index] || set[index].name !== name) {
        set.splice(index, 0, {
          name,
          value: 0,
          wine_name: wines[setIndex]
        });
      }
    });
    if (!multipleRivals) {
      const averagePrice = getAverage(
        set.filter((item) => item.value && item.value.value)
      );
      const averageMaxPrice = getMaxValue(set);
      const averageMinPrice = getMinValue(set);

      if (set.length > 0) {
        set.unshift({
          name: I18n.t('placeholderAll'),
          vintage: I18n.t('placeholderAll'),
          wine_name: wines[setIndex],
          displayed_currency: get(set, '[0].displayed_currency'),
          local_currency: get(set, '[0].local_currency'),
          minPrice: { icon: 'down', value: averageMinPrice },
          value: { icon: 'neutral', value: averagePrice },
          maxPrice: { icon: 'up', value: averageMaxPrice }
        });
      }
    }
  });
  return formattedData;
};

const OnlineSalesBarChart = ({
  title,
  rivalList = [],
  multipleRivals,
  rivalSelection,
  handleRivalSelection,
  allMarket,
  data = [],
  dataClient = [],
  premium,
  vintage,
  handleVintage,
  exportOptions,
  vintageStart,
  vintageEnd,
  showLocalCurrency = false
}) => {
  const [activePill, setActivePill] = useState('avg_price');

  const formattedData = formatData({
    data,
    dataClient,
    allMarket,
    showLocalCurrency,
    multipleRivals,
    rivalSelection,
    vintage,
    vintageEnd,
    vintageStart,
    activePill
  });
  const labels = getLabels(formattedData);
  const maxDataDomain = getDataMaxDomain(formattedData, showLocalCurrency);
  const vintageOptions = getVintageOptions(data, multipleRivals);

  return (
    <Card className="card" id="vente-en-ligne-prix-millesime">
      <OnlineSalesBarChartContainer>
        <CardHeader
          alignItems="center"
          title={
            <div className="row">
              <p>{title}</p>
              <div className="pillsContainer ignored-export">
                <Pills
                  options={[
                    { label: I18n.t('average'), value: 'avg_price' },
                    { label: I18n.t('low'), value: 'low_price' }
                  ].map((item) => ({
                    ...item,
                    active: activePill === item.value
                  }))}
                  onChange={({ value }) => setActivePill(value)}
                />
              </div>
            </div>
          }
          styles={`.cardHeaderRight{width:100%; justify-content: flex-end;};flex-direction:row;align-items:center;`}
          fontSize="18px"
          iconRight="right"
          rightComponents={
            <div className="row ignored-export">
              <ChartInputsBloc
                vintageStart={vintage}
                noVintageEnd={true}
                vintageOptions={vintageOptions}
                onChange={(field, value) => handleVintage(value)}
              />
              <div className="exportButton">
                <ExportButton
                  name={`${
                    get(exportOptions, 'market')
                      ? get(exportOptions, 'market')
                      : 'Tous'
                  }-${get(exportOptions, 'date')}-${get(
                    exportOptions,
                    'wine.label'
                  )}vente-en-ligne-prix-millesime`}
                  jpg
                  pdf
                  targetElement="#vente-en-ligne-prix-millesime"
                />
              </div>
            </div>
          }
        />
        <BarGraph
          data={formattedData}
          height={380}
          firstBarColors={
            multipleRivals ? undefined : ['rgb(156,64,50', 'rgb(59,70,78']
          }
          renderTooltip={getRenderTooltip(showLocalCurrency)}
          tooltipOffset={{ x: 0, y: 40 + -25 * formattedData.length }}
          tooltipOffsetLeft={{ x: 20, y: -80 }}
          tooltipOffsetRight={{ x: -20, y: -10 }}
          labels={labels}
          xAxisProps={{
            interval: 'preserveStart'
          }}
          yAxisProps={{
            domain: [() => 0, () => Number(maxDataDomain)],
            tickFormatter: (value) =>
              value.toLocaleString('fr-FR', {
                style: 'currency',
                currency: showLocalCurrency
                  ? get(formattedData, '[0][0].local_currency', 'EUR')
                  : get(formattedData, '[0][0].displayed_currency', 'EUR'),
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })
          }}
        />
        {premium && (
          <SelectRivals
            multipleRivals={multipleRivals}
            rivalSelection={rivalSelection}
            rivalList={rivalList}
            handleRivalSelection={handleRivalSelection}
          />
        )}
      </OnlineSalesBarChartContainer>
    </Card>
  );
};

export default OnlineSalesBarChart;
